import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import type { LanguageCode, MeResponse, UserPreferences } from '@ampshift/shared';
import { ARABIC_VOICES, ENGLISH_VOICES, updateLocalSettings, type LocalSettings } from '../settings/local';
import { comboFromEvent, describeShortcut, formatShortcut } from '../voice/shortcuts';
import { useT } from '../i18n/useT';

export interface SettingsPanelProps {
  me: MeResponse;
  preferences: UserPreferences;
  local: LocalSettings;
  effective: { shortcut: string; pushToTalkShortcut: string; silenceThresholdMs: number };
  onSavePreferences: (patch: Partial<UserPreferences>) => Promise<void>;
  onClose: () => void;
}

function ShortcutCapture({ value, onChange, label }: { value: string; onChange: (combo: string) => void; label: string }): JSX.Element {
  const t = useT();
  const [capturing, setCapturing] = useState(false);
  useEffect(() => {
    if (!capturing) return;
    const onKey = (e: KeyboardEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      if (e.key === 'Escape') {
        setCapturing(false);
        return;
      }
      const combo = comboFromEvent(e);
      if (!combo) return;
      onChange(formatShortcut(combo));
      setCapturing(false);
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [capturing, onChange]);
  return (
    <div className="setting-row">
      <span className="setting-label">{label}</span>
      <span className="setting-value">
        <kbd>{describeShortcut(value)}</kbd>
        <button type="button" className="btn btn-small" onClick={() => setCapturing((c) => !c)} aria-pressed={capturing}>
          {capturing ? t('pressKeys') : t('change')}
        </button>
      </span>
    </div>
  );
}

export function SettingsPanel(props: SettingsPanelProps): JSX.Element {
  const t = useT();
  const { me, preferences, local, effective } = props;
  const [status, setStatus] = useState<string | undefined>();
  const allowed = me.voiceDefaults.allowedLanguages;
  const allowAuto = allowed.length > 1;
  const save = async (patch: Partial<UserPreferences>): Promise<void> => {
    try {
      await props.onSavePreferences(patch);
      setStatus(t('settingsSaved'));
    } catch {
      setStatus(t('settingsSaveFailed'));
    }
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') props.onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [props]);

  const englishVoices = Array.from(new Set<string>([me.voiceDefaults.englishVoiceId, ...ENGLISH_VOICES]));

  return (
    <div className="settings-backdrop" onClick={props.onClose} role="presentation">
      <aside className="settings" role="dialog" aria-modal="true" aria-label={t('settings')} onClick={(e) => e.stopPropagation()} data-testid="settings-panel">
        <header className="settings-header">
          <h2>{t('settings')}</h2>
          <button type="button" className="icon-btn" aria-label={t('close')} onClick={props.onClose}>
            ✕
          </button>
        </header>
        <div className="settings-body">
          <h3>{t('voiceOutput')}</h3>
          <label className="setting-row">
            <span className="setting-label">{t('voiceOutput')}</span>
            <input type="checkbox" checked={preferences.voiceOutputEnabled} onChange={(e) => void save({ voiceOutputEnabled: e.target.checked })} data-testid="pref-voice-output" />
          </label>
          <label className="setting-row">
            <span className="setting-label">{t('arabicVoice')}</span>
            <select
              value={local.arabicVoiceId}
              onChange={(e) => {
                const v = e.target.value === 'Zayd' ? 'Zayd' : 'Hala';
                updateLocalSettings({ arabicVoiceId: v });
                if (local.uiLanguage === 'ar') void save({ preferredVoiceId: v });
              }}
            >
              {ARABIC_VOICES.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label className="setting-row">
            <span className="setting-label">{t('englishVoice')}</span>
            <select
              value={local.englishVoiceId}
              onChange={(e) => {
                updateLocalSettings({ englishVoiceId: e.target.value });
                if (local.uiLanguage === 'en') void save({ preferredVoiceId: e.target.value });
              }}
            >
              {englishVoices.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>

          <h3>{t('listening')}</h3>
          <label className="setting-row">
            <span className="setting-label">{t('inputLanguage')}</span>
            <select
              value={local.inputLanguage}
              onChange={(e) => {
                const v = e.target.value as LanguageCode | 'auto';
                updateLocalSettings({ inputLanguage: v });
                if (v !== 'auto') void save({ preferredLanguage: v });
              }}
              data-testid="pref-input-language"
            >
              {allowed.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
              {allowAuto ? <option value="auto">{t('auto')}</option> : null}
            </select>
          </label>
          <label className="setting-row">
            <span className="setting-label">{t('listeningMode')}</span>
            <select value={preferences.listeningMode} onChange={(e) => void save({ listeningMode: e.target.value === 'PRESS_AND_HOLD' ? 'PRESS_AND_HOLD' : 'PRESS_TO_TOGGLE' })} data-testid="pref-listening-mode">
              <option value="PRESS_TO_TOGGLE">{t('pressToToggle')}</option>
              <option value="PRESS_AND_HOLD">{t('pressAndHold')}</option>
            </select>
          </label>
          <ShortcutCapture label={t('toggleShortcut')} value={effective.shortcut} onChange={(combo) => void save({ shortcut: combo })} />
          <ShortcutCapture label={t('pushToTalkShortcut')} value={effective.pushToTalkShortcut} onChange={(combo) => updateLocalSettings({ pushToTalkShortcut: combo })} />
          <p className="muted small">{t('shortcutCaveat')}</p>
          <label className="setting-row">
            <span className="setting-label">
              {t('silenceThreshold')} <span className="muted">{effective.silenceThresholdMs} ms</span>
            </span>
            <input
              type="range"
              min={300}
              max={3000}
              step={50}
              value={effective.silenceThresholdMs}
              onChange={(e) => void save({ silenceThresholdMs: Number(e.target.value) })}
              data-testid="pref-silence"
            />
          </label>

          <h3>{t('language')}</h3>
          <label className="setting-row">
            <span className="setting-label">{t('uiLanguage')}</span>
            <select value={local.uiLanguage} onChange={(e) => updateLocalSettings({ uiLanguage: e.target.value === 'ar' ? 'ar' : 'en' })}>
              <option value="en">English</option>
              <option value="ar">العربية</option>
            </select>
          </label>
          <label className="setting-row">
            <span className="setting-label">{t('appearance')}</span>
            <select value={local.theme} onChange={(e) => updateLocalSettings({ theme: e.target.value === 'dark' ? 'dark' : e.target.value === 'light' ? 'light' : 'system' })} data-testid="theme-select">
              <option value="system">{t('themeSystem')}</option>
              <option value="light">{t('themeLight')}</option>
              <option value="dark">{t('themeDark')}</option>
            </select>
          </label>
          <div className="setting-row">
            <button
              type="button"
              className="btn btn-small"
              onClick={() => {
                updateLocalSettings({ pushToTalkShortcut: undefined, inputLanguage: me.voiceDefaults.defaultLanguage as LanguageCode });
                void save({ shortcut: me.voiceDefaults.shortcut, silenceThresholdMs: me.voiceDefaults.silenceThresholdMs, listeningMode: 'PRESS_TO_TOGGLE' });
              }}
            >
              {t('reset')}
            </button>
            {status ? <span className="muted small">{status}</span> : null}
          </div>
          <p className="muted small">{t('privacyNote')}</p>
        </div>
      </aside>
    </div>
  );
}
